import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "../Header";
import Footer from "../Footer";
import { Link } from "react-router-dom";
import config from "../common/Config";
import { toast } from "sonner";
import { setTableWidths, getTableWidths } from "../../data/postAPi";
import {
  Box,
  Typography,
  Button,
  Paper,
  TablePagination,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Skeleton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Grid2,
  InputAdornment,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify/iconify';
import '../common/commonStyles.css';


const isRhpTenant = () =>
  config.PREFIX === "rhp" ||
  localStorage.getItem("accessToken") === "rhp" ||
  localStorage.getItem("accessToken") === "hp";

const ReturnList = () => {
  const tableRef = useRef(null);
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const returnViewPath = isRhpTenant() ? "returns/view_rhp" : "returns/view";
  const userId = localStorage.getItem("userId");
  const tableName = "returnListTable";
  const canAcceptReturn = true;
  const canRejectReturn = true;

  const [statusFilter, setStatusFilter] = useState("Pending");
  const [editModal, setEditModal] = useState({
    open: false,
    id: null,
    amount: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalReturns, setTotalReturns] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const resizeRef = useRef({
    isResizing: false,
    startX: 0,
    startWidth: 0,
    columnIndex: -1,
  });

  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  const debouncedSetTableWidths = useRef(
    debounce((userId, tableName, widths) => {
      setTableWidths(userId, tableName, widths);
    }, 500)
  ).current;

  useEffect(() => {
    async function fetchWidths() {
      try {
        const res = await getTableWidths(userId, tableName);
        if (res?.data?.widths && Array.isArray(res.data.widths) && res.data.widths.length > 0) {
          debouncedSetTableWidths(userId, tableName, res.data.widths);
        }
      } catch (e) {
      }
    }
    fetchWidths();
  }, []);

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("draw", String(page + 1));
      formData.append("start", String(page * rowsPerPage));
      formData.append("length", String(rowsPerPage));
      formData.append("status", statusFilter || "");
      formData.append("search[value]", searchTerm || "");

      const response = await fetch(`${config.API_URL}/return/returnList_v2`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status} Error`;
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorJson = JSON.parse(errorText);
              errorMessage = errorJson.message || errorJson.error || errorText.substring(0, 300);
            } catch {
              errorMessage = errorText.substring(0, 300);
            }
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${e.message}`;
        }
        throw new Error(errorMessage);
      }

      const json = await response.json();

      if (json?.data && Array.isArray(json.data)) {
        setReturns(json.data);
        setTotalReturns(json.recordsFiltered || json.recordsTotal || json.data.length);
      } else {
        setReturns([]);
        setTotalReturns(0);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch return list");
      setReturns([]);
      setTotalReturns(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, accessToken, searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startResizing = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      isResizing: true,
      startX: e.clientX,
      startWidth: getTableWidths(userId, tableName)[index],
      columnIndex: index,
    };

    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", stopResize);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleResize = (e) => {
    if (!resizeRef.current.isResizing) return;

    const { startX, startWidth, columnIndex } = resizeRef.current;
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff);

    debouncedSetTableWidths(userId, tableName, [...getTableWidths(userId, tableName)]);
  };

  const stopResize = () => {
    resizeRef.current.isResizing = false;
    document.removeEventListener("mousemove", handleResize);
    document.removeEventListener("mouseup", stopResize);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  const handleTabChange = (event, newValue) => {
    const statusMap = { 0: "Pending", 1: "Accepted", 2: "Rejected" };
    setStatusFilter(statusMap[newValue]);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handelAcceptReject = (id, action) => {
    const confirmMessage =
      action === "accept"
        ? "Are you sure you want to create the Credit Note?"
        : "Are you sure you want to reject this order?";

    setConfirmDialog({
      open: true,
      title: "Are you sure?",
      message: confirmMessage,
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        const url =
          action === "accept"
            ? `${config.API_URL}/add_accept_return`
            : `${config.API_URL}/add_reject_return`;

        const formData = new FormData();
        formData.append("id", id);
        if (action === "accept") {
          formData.append("allocation", "yes");
        }

        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          });

          const data = await response.json();
          if (data.success) {
            toast.success(data.message || "Action completed successfully.");
            fetchData();
          } else {
            toast.error(data.message || "An error occurred.");
          }
        } catch (error) {
          toast.error("API request failed.");
        }
      },
    });
  };

  const showEditAmountPopup = (id, currentAmount) => {
    setEditModal({
      open: true,
      id,
      amount: currentAmount,
    });
  };

  const handleEditAmountClose = () => {
    setEditModal({ open: false, id: null, amount: "" });
  };

  const handleEditAmountSubmit = async (e) => {
    if (e) e.preventDefault();
    const userId = localStorage.getItem("userId") || "";
    const amount = editModal.amount;
    if (!amount || isNaN(amount) || parseFloat(amount) < 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("id", editModal.id);
      formData.append("user_id", userId);
      formData.append("amount", amount);

      handleEditAmountClose();

      const response = await fetch(`${config.API_URL}/return/update_credit_amount`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || "Amount updated successfully.");
        fetchData();
      } else {
        toast.error(data.message || "An error occurred.");
      }
    } catch (error) {
      toast.error("API request failed.");
    }
  };

  const getHeaders = () => {
    const headers = [{"label": "Date", "width": 120}, {"label": "Return No", "width": 120}, {"label": "Customer (Ac/No)", "width": 300}, {"label": "Grand Total", "width": 130}, {"label": "Credit Note", "width": 130}, {"label": "Total Items", "width": 130}, {"label": "Return By", "width": 130}, {"label": "Status", "width": 120}, statusFilter === "Pending" && (canAcceptReturn || canRejectReturn) ? {"label": "Actions", "width": 120} : null];
    return headers.filter(header => header !== null);
  };

  const getTabValue = () => {
    const statusMap = { "Pending": 0, "Accepted": 1, "Rejected": 2 };
    return statusMap[statusFilter] || 0;
  };

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style" >
          <Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center" gap={1} px={2} py={1.5}>
            <Grid2 size={{ xs: 12, md: 2 }}>
              <Typography className="page-title">
                Return List
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Box>
                <Tabs
                  value={getTabValue()}
                  onChange={handleTabChange}
                  className="common-tabs"
                >
                  <Tab label="Pending" />
                  <Tab label="Accepted" />
                  <Tab label="Rejected" />
                </Tabs>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
              <TextField
                className="input-border"
                variant="outlined"
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
               sx={{ width: { xs: '100%', md: 'auto' } }}
                variant="contained"
                href={`${config.BASE_URL}/return/${
                  config.PREFIX === "rhp" ||
                  localStorage.getItem("accessToken") === "rhp" ||
                  localStorage.getItem("accessToken") === "hp"
                    ? "add_return_rhp"
                    : "add_return"
                }`}
                startIcon={<Iconify icon="mingcute:add-line" width={16} />}
                className="button-variant-contained"
              >
                Add Return
              </Button>
            </Grid2>
          </Grid2>
          <TableContainer
            ref={tableRef}>
            <Table
              size="medium"
              className="table-cell-border"
              sx={{ tableLayout: "fixed" }}
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  {getHeaders().map((header, idx) => (
                    <TableCell
                      key={header}
                      align={idx === 0 || idx === getHeaders().length - 1 ? "left" : "left"}
                      sx={{
                        width: header.width ? `${header.width}px` : "auto",
                      }}
                    >
                      {header.label}
                      {idx < getHeaders().length - 1 && (
                        <Box
                          onMouseDown={(e) => startResizing(idx, e)}
                          sx={{
                            position: "absolute",
                            right: -2,
                            top: 0,
                            width: 5,
                            height: "100%",
                            cursor: "col-resize",
                            backgroundColor: "transparent",
                            zIndex: 10,
                            userSelect: "none",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.1)",
                            },
                          }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={getHeaders().length} sx={{ py: 1.5 }}>
                        <Skeleton height={40} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : returns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={getHeaders().length} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  returns.map((row, index) => (
                    <TableRow
                      key={row.id}
                      hover
                      className="table-cell-style"
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(145, 158, 171, 0.04)",
                        },
                      }}
                    >
                      <TableCell align="left">
                        {row.date}
                      </TableCell>
                      <TableCell align="left">
                        <Box
                          component="a"
                          href={`${config.BASE_URL}/${returnViewPath}/${row.id}`}
                          sx={{
                            color: "#212b36",
                            textDecoration: "underline !important",
                            "&:hover": {
                              textDecoration: "underline !important",
                            },
                          }}
                        >
                          {row.reference_no}
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        {row.company}
                      </TableCell>
                      <TableCell align="left">
                        {row.grand_total}
                      </TableCell>
                      <TableCell align="left">
                        {isNaN(row.credit_note) ? (
                          row.credit_note
                        ) : statusFilter === "Pending" ? (
                          <Box
                            component="a"
                            href="javascript:void(0);"
                            onClick={() => showEditAmountPopup(row.id, row.credit_note)}
                            sx={{
                              color: "#212b36",
                              textDecoration: "underline !important",
                              cursor: "pointer",
                              "&:hover": {
                                textDecoration: "underline !important",
                              },
                            }}
                            title="Edit Amount"
                          >
                            {parseFloat(row.credit_note).toFixed(2)}
                          </Box>
                        ) : (
                          parseFloat(row.credit_note).toFixed(2)
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {row.total_items}
                      </TableCell>
                      <TableCell align="left">
                        {row.return_by}
                      </TableCell>
                      <TableCell align="left">
                        {row.status}
                      </TableCell>
                      {statusFilter === "Pending" && (canAcceptReturn || canRejectReturn) && (
                        <TableCell align="left">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            {canAcceptReturn && (
                              <IconButton
                                size="small"
                                onClick={() => handelAcceptReject(row.id, "accept")}
                                sx={{
                                  color: "#00a76f",
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 167, 111, 0.08)",
                                  },
                                }}
                                title="Accept Return"
                              >
                                <Iconify icon="bi:check-square-fill" width={18} height={18} />
                              </IconButton>
                            )}
                            {canRejectReturn && (
                              <IconButton
                                size="small"
                                onClick={() => handelAcceptReject(row.id, "reject")}
                                sx={{
                                  color: "#ff5630",
                                  "&:hover": {
                                    backgroundColor: "rgba(255, 86, 48, 0.08)",
                                  },
                                }}
                                title="Reject Return"
                              >
                                <Iconify icon="bi:sign-turn-right-fill" width={18} height={18} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {returns.length > 0 && (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <TablePagination
                component="div"
                count={totalReturns}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={editModal.open}
        onClose={handleEditAmountClose}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleEditAmountSubmit}>
          <DialogTitle>Edit Credit Note</DialogTitle>
          <DialogContent>
            <TextField
              className="form-input-border"
              autoFocus
              margin="dense"
              label="Amount"
              type="number"
              fullWidth
              variant="outlined"
              value={editModal.amount}
              onChange={(e) => setEditModal({ ...editModal, amount: e.target.value })}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditAmountClose} variant="outlined" className="button-variant-outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained" className="button-variant-contained">
              Update
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
            color="inherit"
            variant="outlined"
            className="button-variant-outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.onConfirm) {
                confirmDialog.onConfirm();
              }
            }}
            variant="contained"
            className="button-variant-contained"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default ReturnList;